'use client'

import React, { useCallback, useEffect, useRef, useState } from 'react'
import { cn } from '@/utilities/ui'

type VideoSource = {
  src: string
  type?: string
}

export type VideoPlayerClientProps = {
  sources: VideoSource[]
  poster?: string
  label?: string
  playbackMode: 'player' | 'ambient'
  /** True when no aspect ratio is enforced — the video's own dimensions set the height. */
  intrinsicHeight?: boolean
  /** Width/height ratio reserved while the video is still unloaded, to avoid layout shift. */
  reservedRatio?: number
  className?: string
}

export const VideoPlayerClient: React.FC<VideoPlayerClientProps> = ({
  sources,
  poster,
  label,
  playbackMode,
  intrinsicHeight = false,
  reservedRatio,
  className,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [activated, setActivated] = useState(false)

  // Ambient loops only start once they scroll into view, so a video far down
  // the page never competes with the initial render for bandwidth.
  useEffect(() => {
    if (playbackMode !== 'ambient') return
    const video = videoRef.current
    if (!video) return

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return
        if (entry.isIntersecting) {
          const result = video.play()
          // iOS Low Power Mode / Data Saver reject autoplay — the poster stays.
          if (result && typeof result.then === 'function') result.catch(() => {})
        } else {
          video.pause()
        }
      },
      { threshold: 0.25 },
    )

    observer.observe(video)
    return () => observer.disconnect()
  }, [playbackMode])

  const handleActivate = useCallback(() => {
    const video = videoRef.current
    if (!video) return
    setActivated(true)
    video.play()?.catch(() => {})
  }, [])

  const isAmbient = playbackMode === 'ambient'

  // Height comes from the aspect-ratio class passed in via `className` — an
  // explicit h-full on the wrapper would override it.
  return (
    <div className={cn('relative w-full overflow-hidden bg-dark', className)}>
      <video
        ref={videoRef}
        className={cn('w-full', intrinsicHeight ? 'h-auto' : 'h-full object-cover')}
        // Without a poster and with preload="none" an unloaded <video> falls back to
        // 300×150, then jumps to its real size once metadata arrives.
        style={intrinsicHeight && reservedRatio ? { aspectRatio: reservedRatio } : undefined}
        poster={poster}
        preload={isAmbient ? 'metadata' : 'none'}
        playsInline
        muted={isAmbient}
        loop={isAmbient}
        controls={!isAmbient && activated}
        aria-label={label}
      >
        {sources.map((source) => (
          <source key={source.src} src={source.src} type={source.type} />
        ))}
      </video>

      {!isAmbient && !activated && (
        <button
          type="button"
          onClick={handleActivate}
          aria-label={label ? `Video abspielen: ${label}` : 'Video abspielen'}
          className="group absolute inset-0 flex items-center justify-center bg-black/25 transition-colors hover:bg-black/40 focus-visible:outline-2 focus-visible:outline-offset-[-4px] focus-visible:outline-white"
        >
          <span className="flex h-20 w-20 items-center justify-center bg-brand text-white transition-transform duration-300 group-hover:scale-110">
            <svg
              aria-hidden="true"
              viewBox="0 0 24 24"
              fill="currentColor"
              className="ml-1 h-8 w-8"
            >
              <path d="M8 5v14l11-7z" />
            </svg>
          </span>
        </button>
      )}
    </div>
  )
}

export default VideoPlayerClient
